import { apiClient } from './api.client';

const base = '/camera-security/face-matching';
export type MatchingProvider = 'local' | 'rekognition';
export interface MatchingSettings { enabled: boolean; threshold?: number; min_margin?: number; provider?: MatchingProvider; }
export interface MatchingStatus extends MatchingSettings {
  threshold: number; min_margin: number;
  configured: boolean; can_manage: boolean; indexed_photos: number;
  failed_references: number; pending_jobs: number; review_required: boolean;
  provider?: MatchingProvider; provider_selectable?: boolean; pending_removals?: number; error?: string;
  image_metadata?: ImageMetadataStatus;
  allowed_providers?: MatchingProvider[]; platform_allowed?: boolean;
}
export interface ImageMetadataStatus {
  enabled: boolean; configured: boolean; mode: 'off' | 'shadow' | 'active';
  pending_jobs: number; failed_jobs: number; completed_jobs: number; last_error?: string | null;
  latest_preview?: { event_id: number; description: string; warnings: string[]; mode: string };
}
export interface FaceMatch {
  id: string; person_id: number; person_name: string; similarity: number;
  ambiguous: boolean; review_status: 'pending' | 'confirmed' | 'rejected'; face_index: number;
  confirmation_source?: 'automatic' | 'reviewer' | null;
}
export interface EventMatches {
  enabled: boolean; can_manage?: boolean;
  status: 'disabled' | 'not_queued' | 'pending' | 'processing' | 'completed' | 'failed';
  error?: string; matches: FaceMatch[];
  face_count?: number | null;
  provider_mismatch?: boolean;
  diagnostics?: {
    outcome: 'matched' | 'no_faces_detected' | 'no_searchable_faces' | 'below_threshold' | 'no_registry_match';
    searched_faces: number; skipped_small_faces: number; skipped_low_confidence_faces: number;
    best_similarity: number | null; threshold: number;
  } | null;
}
export interface PersonMatch extends Pick<FaceMatch, 'id' | 'similarity' | 'ambiguous' | 'review_status'> {
  event_id: number; event_time: string | null; event_tag: string; location_name: string;
}
export interface PersonHistoryFilters { start_time: string; end_time: string; page: number; size: number; }
export interface PersonHistoryPage { items: PersonMatch[]; page: number; size: number; has_next: boolean; }
export interface ReferenceStatus { face_image_id: number; status: string; last_error?: string; }
export const faceMatchingService = {
  status: () => apiClient.get<MatchingStatus>(`${base}/status`),
  settings: (data: MatchingSettings) => apiClient.put<MatchingStatus>(`${base}/settings`, data),
  metadataSettings: (enabled: boolean) => apiClient.put<ImageMetadataStatus>(`${base}/metadata/settings`, { enabled }),
  event: (id: number) => apiClient.get<EventMatches>(`${base}/events/${id}`),
  queue: (id: number) => apiClient.post(`${base}/events/${id}/queue`),
  review: (id: string, decision: 'confirmed' | 'rejected') => apiClient.put(`${base}/matches/${id}/review`, { decision }),
  history: (id: number, filters: PersonHistoryFilters, signal?: AbortSignal) => apiClient.get<PersonHistoryPage>(`${base}/persons/${id}/events`, filters, { signal }),
  references: (id: number, signal?: AbortSignal) => apiClient.get<{ items: ReferenceStatus[]; can_manage?: boolean }>(`${base}/persons/${id}/references`, undefined, { signal }),
  retryReference: (personId: number, photoId: number) => apiClient.post<{ queued: boolean }>(`${base}/persons/${personId}/references/${photoId}/retry`),
};

export function matchingError(error: any): string {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : error?.message || 'Unable to update face matching';
}
